import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PublicHeader from '../../components/navigation/PublicHeader';
import RouteMap from './components/RouteMap';

import TripsTable from './components/TripsTable';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { routeService } from '../../services/routeService';
import { scheduleService } from '../../services/scheduleService';
import { calculateETAWithLocation } from '../../utils/etaCalculator';


const RouteDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeId = searchParams?.get('id');

  const [showPolyline, setShowPolyline] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userLocation, setUserLocation] = useState(null);
  const [routeData, setRouteData] = useState(null);
  const [stops, setStops] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (routeId) {
      loadRouteData();
    }
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation?.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position?.coords?.latitude,
            longitude: position?.coords?.longitude
          });
        },
        (error) => {
          console.error('Location error:', error);
          // Fallback to default location
          setUserLocation({
            latitude: 37.8746,
            longitude: 32.4932
          });
        }
      );
    } else {
      // Fallback to default location
      setUserLocation({
        latitude: 37.8746,
        longitude: 32.4932
      });
    }
  }, [routeId]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadRouteData = async () => {
    try {
      setLoading(true);
      const [route, routeSchedules] = await Promise.all([
        routeService?.getById(routeId),
        scheduleService?.getByRouteId(routeId)
      ]);

      // Keep polyline in original array format [[lng, lat], [lng, lat]]
      const polylineCoords = route?.geometry?.coordinates || [];

      setRouteData({
        id: route?.id,
        routeName: route?.routeName,
        startPoint: {
          name: route?.startPoint,
          latitude: polylineCoords?.[0]?.[1] || 37.8746,
          longitude: polylineCoords?.[0]?.[0] || 32.4932
        },
        endPoint: {
          name: route?.endPoint,
          latitude: polylineCoords?.[polylineCoords?.length - 1]?.[1] || 37.8856,
          longitude: polylineCoords?.[polylineCoords?.length - 1]?.[0] || 32.5142
        },
        polyline: polylineCoords,
        stops: routeSchedules?.map(stop => ({
          id: stop?.id,
          name: stop?.stopName,
          latitude: stop?.latitude,
          longitude: stop?.longitude,
          orderIndex: stop?.orderIndex
        })),
        distanceMeters: (route?.distanceKm || 0) * 1000,
        durationMinutes: route?.durationMinutes || 25
      });

      setStops(routeSchedules);
      setSchedules(routeSchedules);
    } catch (err) {
      setError(err?.message || 'Rota bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    // Implement search functionality if needed
    console.log('Search:', query);
  };

  const handleSearchChange = (value) => {
    // Implement search change handler if needed
    console.log('Search change:', value);
  };

  const calculateETA = (startTime, endTime, currentProgress = 0) => {
    if (!userLocation || !routeData?.polyline) {
      return 0;
    }

    const result = calculateETAWithLocation({
      userLocation,
      routePolyline: routeData?.polyline,
      departureTime: startTime,
      routeDurationMinutes: routeData?.durationMinutes || 25,
      currentTime
    });

    return result?.etaMinutes;
  };

  const getTripStatus = (startTime, endTime) => {
    const now = currentTime;
    const [startHour, startMinute] = startTime?.split(':')?.map(Number);
    const [endHour, endMinute] = endTime?.split(':')?.map(Number);
    
    const startDate = new Date(now);
    startDate?.setHours(startHour, startMinute, 0, 0);
    
    const endDate = new Date(now);
    endDate?.setHours(endHour, endMinute, 0, 0);
    
    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'passed';
    return 'active';
  };

  const mockTrips = schedules?.flatMap(schedule => {
    const trips = [];
    const [startHour, startMinute] = schedule?.startTime?.split(':')?.map(Number);
    const [endHour, endMinute] = schedule?.endTime?.split(':')?.map(Number);
    
    let currentMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    
    while (currentMinutes <= endMinutes) {
      const hours = Math.floor(currentMinutes / 60);
      const minutes = currentMinutes % 60;
      const startTime = `${String(hours)?.padStart(2, '0')}:${String(minutes)?.padStart(2, '0')}`;
      const endTime = `${String(Math.floor((currentMinutes + schedule?.durationMinutes) / 60))?.padStart(2, '0')}:${String((currentMinutes + schedule?.durationMinutes) % 60)?.padStart(2, '0')}`;
      
      trips?.push({
        id: `${schedule?.id}-${currentMinutes}`,
        startTime,
        endTime,
        status: getTripStatus(startTime, endTime),
        eta: calculateETA(startTime, endTime)
      });
      
      currentMinutes += schedule?.frequencyMinutes;
    }
    
    return trips;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Rota bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Rota bilgileri yüklenemedi</p>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/home');
  };

  const handleTogglePolyline = () => {
    setShowPolyline(!showPolyline);
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader onSearch={handleSearch} onSearchChange={handleSearchChange} />
      <main className="pt-20 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={handleBack}
              aria-label="Geri Dön"
            >
              <Icon name="ArrowLeft" size={20} color="var(--color-foreground)" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
                {routeData?.routeName}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">
                {routeData?.startPoint?.name} → {routeData?.endPoint?.name}
              </p>
            </div>
          </div>

          <RouteMap
            routeName={routeData?.routeName}
            routePolyline={routeData?.polyline}
            startPoint={routeData?.startPoint}
            endPoint={routeData?.endPoint}
            stops={routeData?.stops}
            userLocation={userLocation}
            showPolyline={showPolyline}
            onTogglePolyline={handleTogglePolyline}
          />

          <div className="mb-8">
            <TripsTable
              trips={mockTrips}
              userLocation={userLocation}
            />
          </div>

          <div className="bg-card rounded-xl border border-border p-4 md:p-6">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
                <Icon name="Info" size={20} color="var(--color-primary)" />
              </div>
              <div className="space-y-2">
                <h3 className="text-base md:text-lg font-semibold text-foreground">
                  Tahmin Bilgilendirmesi
                </h3>
                <p className="text-sm text-muted-foreground">
                  Bu sayfada gösterilen varış süreleri, programlanmış kalkış/varış saatlerine ve rota geometrisine dayanarak hesaplanmaktadır. 
                  Araçların gerçek zamanlı GPS takibi yapılmamaktadır. Trafik yoğunluğu, hava koşulları ve diğer faktörler 
                  nedeniyle gerçek varış süreleri tahminlerden farklılık gösterebilir.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                    <Icon name="Clock" size={14} color="var(--color-muted-foreground)" />
                    <span className="text-xs text-muted-foreground">Programlı Saatler</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                    <Icon name="Map" size={14} color="var(--color-muted-foreground)" />
                    <span className="text-xs text-muted-foreground">Rota Geometrisi</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                    <Icon name="TrendingUp" size={14} color="var(--color-muted-foreground)" />
                    <span className="text-xs text-muted-foreground">Trafik Çarpanı</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <footer className="bg-card border-t border-border py-6 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name="Bus" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Konya Dolmuş Saatim</p>
                <p className="text-xs text-muted-foreground">Akıllı Ulaşım Çözümü</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center md:text-right">
              © {new Date()?.getFullYear()} Tüm hakları saklıdır. Bu uygulama bilgilendirme amaçlıdır.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RouteDetail;